import React, { Component } from 'react';
import { Button, Dialog, DialogTitle, DialogContent } from '@material-ui/core';
import { withDataProvider } from 'react-admin';

/**
 * Plaid Bank Account Link Component
 * Implements Paynote Plaid Instant Verification steps 2-4
 * 
 * Step 2: Get Plaid Link URL from account API (backend assembles the URL)
 * Step 3: Send the Link to Your Customer (or embed in iframe)
 * Step 4: Listen for Verification
 */
class PlaidBankAccountLink extends Component {
    state = {
        open: false,
        plaidUrl: '',
        verificationStatus: "",
    };

    componentDidMount() {
        this.fetchPlaidUrl();
        this.setupMessageListener();
    }

    componentWillUnmount() {
        this.removeMessageListener();
        if (this.verificationInterval) {
            clearInterval(this.verificationInterval);
        }
    }

    /**
     * Build complete Plaid URL with successUrl and cancelUrl parameters
     * @param {string} plaidLink - Base plaid link URL from backend
     * @returns {string} Complete URL with success and cancel URLs
     */
    buildCompletePlaidUrl = (plaidLink) => {
        if (!plaidLink) {
            return null;
        }

        // Build success and cancel URLs pointing to dashboard
        const currentUrl = window.location.href.split('#')[0];
        const successUrl = `${currentUrl}#/dashboard?plaid=success`;
        const cancelUrl = `${currentUrl}#/dashboard?plaid=cancel`;
        return `${plaidLink}?successUrl=${successUrl}&cancelUrl=${cancelUrl}`;
    };

    /**
     * Step 2: Get Plaid Link URL from account API
     * Backend returns plaidLink, we add successUrl and cancelUrl parameters
     */
    async fetchPlaidUrl() {
        const { plaidLink } = this.props;
        const completeUrl = this.buildCompletePlaidUrl(plaidLink);
        if (completeUrl) {
            this.setState({ plaidUrl: completeUrl });
        }
    }

    componentDidUpdate(prevProps) {
        // Update if plaidUrl prop changes
        if (this.props.plaidLink !== prevProps.plaidLink && this.props.plaidLink) {
            const completeUrl = this.buildCompletePlaidUrl(this.props.plaidLink);
            console.log('completeUrl', completeUrl);
            if (completeUrl) {
                this.setState({ plaidUrl: completeUrl });
            }
        }
    }

    /**
     * Step 3: Open Plaid Link in iframe or new window
     */
    handleOpenPlaidLink = () => {
        if (!this.state.plaidUrl) {
            alert('Plaid link URL is not available. Please check your configuration.');
            return;
        }
        this.setState({ open: true });
    };

    handleClose = () => {
        this.setState({ open: false });
        if (this.verificationInterval) {
            clearInterval(this.verificationInterval);
        }
    };

    /**
     * Step 4: Listen for Verification
     * Setup message listener for iframe communication
     */
    setupMessageListener = () => {
        this.messageHandler = (event) => {
            // Verify origin for security
            const allowedOrigins = [
                'https://paynote.seamlesschex.com',
                'https://paynote-sandbox.seamlesschex.com'
            ];

            if (!allowedOrigins.some(origin => event.origin.includes(origin))) {
                return;
            }
            if (event.data.event === 'callSuccessFunction') {
                this.handleVerificationSuccess(event.data);
            } else if (event.data.event === 'callErrorFunction') {
                this.handleVerificationError(event.data);
            } else if (event.data.event === 'callCloseFunction') {
                this.handleClose();
            }
        };

        window.addEventListener('message', this.messageHandler);
    };

    removeMessageListener = () => {
        if (this.messageHandler) {
            window.removeEventListener('message', this.messageHandler);
        }
    };

    /**
     * Handle successful verification
     */
    handleVerificationSuccess = (data) => {
        this.setState({ 
            verificationStatus: 'verified',
        });
        
        // Close the dialog after a short delay
        setTimeout(() => {
            this.handleClose();
            // Optionally refresh account data
            if (this.props.onVerificationSuccess) {
                this.props.onVerificationSuccess(data);
            }
        }, 2000);
    };

    /**
     * Handle verification error
     */
    handleVerificationError = (data) => {
        console.error('Bank account linking failed:', data);
        this.setState({ 
            verificationStatus: 'error',
        });
    };

   

    render() {
        const { open, plaidUrl, verificationStatus } = this.state;

        // Hide component if plaidUrl is not available
        if (!plaidUrl) {
            return null;
        }

        return (
            <div>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={this.handleOpenPlaidLink}
                >
                    Connect Bank Account via Plaid
                </Button>

                <Dialog
                    open={open}
                    onClose={this.handleClose}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{
                        style: {
                            width: '100%',
                            height: '100%',
                            maxWidth: '412px',
                            maxHeight: '721px',
                            margin: 0,
                        }
                    }}
                >
                    <DialogTitle>
                        Connect Your Bank Account
                        {verificationStatus === 'verified' && (
                            <span style={{ color: 'green', marginLeft: '10px' }}>
                                ✓ Verified
                            </span>
                        )}
                        {verificationStatus === 'error' && (
                            <span style={{ color: 'red', marginLeft: '10px' }}>
                                ✗ Error
                            </span>
                        )}
                    </DialogTitle>
                    <DialogContent style={{ padding: 0, height: '100%' }}>
                        {plaidUrl && (
                            <iframe
                                id="plaid_iframe"
                                src={plaidUrl}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    border: 'none',
                                    minHeight: '600px',
                                }}
                                title="Plaid Bank Account Link"
                            />
                        )}
    
                    </DialogContent>
                </Dialog>
            </div>
        );
    }
}

export default withDataProvider(PlaidBankAccountLink);

